<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="1100"
    scrollable
    transition="dialog-bottom-transition"
  >
    <v-card class="ds-dialog">
      <!-- Cabeçalho gradient -->
      <v-card-title class="ds-dialog-header py-4">
        <div class="d-flex align-center" style="position: relative; z-index: 1">
          <v-icon color="white" class="mr-2">mdi-clipboard-text-outline</v-icon>
          <div>
            <div class="ds-dialog-title">
              {{ headerTitle }}
            </div>
            <div class="ds-dialog-subtitle">{{ headerSubtitle }}</div>
          </div>
        </div>
        <v-spacer />
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              color="white"
              class="close-btn"
              v-bind="attrs"
              v-on="on"
              @click="close"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>
          <span>Fechar</span>
        </v-tooltip>
      </v-card-title>

      <v-card-text class="ds-dialog-body">
        <!-- Loading -->
        <section
          v-if="loading"
          class="ds-form-section d-flex flex-column align-center justify-center py-12"
        >
          <v-progress-circular indeterminate color="primary" size="48" />
          <p class="mt-3 mb-0 grey--text">Carregando respostas da anamnese...</p>
        </section>

        <!-- Sem dados -->
        <section
          v-else-if="!hasData"
          class="ds-form-section d-flex flex-column align-center justify-center py-12"
        >
          <v-icon size="56" color="grey lighten-1">
            mdi-message-text-outline
          </v-icon>
          <h3 class="mt-3 grey--text text--darken-1">
            Nenhuma resposta encontrada
          </h3>
          <p class="grey--text text-caption mb-0">
            Esta anamnese ainda não possui respostas registradas.
          </p>
        </section>

        <!-- Cards por seção -->
        <div v-else>
          <!-- Resumo no topo -->
          <div class="summary-pill-row">
            <span class="summary-pill">
              <v-icon size="14" class="mr-1">mdi-counter</v-icon>
              {{ histories.length }} resposta{{
                histories.length === 1 ? "" : "s"
              }}
            </span>
            <span class="summary-pill">
              <v-icon size="14" class="mr-1">mdi-shape-outline</v-icon>
              {{ visibleSections.length }} categoria{{
                visibleSections.length === 1 ? "" : "s"
              }}
            </span>
            <span v-if="anamnese?.session_id" class="summary-pill">
              <v-icon size="14" class="mr-1">mdi-identifier</v-icon>
              {{ truncate(anamnese.session_id, 18) }}
            </span>
          </div>

          <v-row dense>
            <v-col
              v-for="section in visibleSections"
              :key="section.key"
              cols="12"
              md="6"
            >
              <section class="ds-form-section ds-form-section--flush section-card">
                <div class="ds-form-section__head">
                  <div class="ds-form-section__title">
                    <v-icon small color="primary" class="mr-2">{{ section.icon }}</v-icon>
                    <span>{{ section.title }}</span>
                  </div>
                  <span class="ds-form-section__aside">{{ section.items.length }}</span>
                </div>

                <div class="section-content">
                  <v-list dense class="py-0">
                    <div
                      v-for="(item, index) in section.items"
                      :key="item.id"
                    >
                      <v-list-item class="px-4 py-1">
                        <v-list-item-icon class="mr-3 my-auto">
                          <v-icon small color="primary">
                            mdi-checkbox-marked-circle-outline
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="text-wrap item-text">
                            {{ getDisplayValue(item) }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider
                        v-if="index < section.items.length - 1"
                        class="mx-4"
                      />
                    </div>
                  </v-list>
                </div>
              </section>
            </v-col>

            <!-- Categoria "Outras" para perguntas não mapeadas -->
            <v-col v-if="unmappedItems.length > 0" cols="12" md="6">
              <section class="ds-form-section ds-form-section--flush section-card">
                <div class="ds-form-section__head">
                  <div class="ds-form-section__title">
                    <v-icon small color="primary" class="mr-2">mdi-dots-horizontal-circle-outline</v-icon>
                    <span>Outras informações</span>
                  </div>
                  <span class="ds-form-section__aside">{{ unmappedItems.length }}</span>
                </div>

                <div class="section-content">
                  <v-list dense class="py-0">
                    <div
                      v-for="(item, index) in unmappedItems"
                      :key="item.id"
                    >
                      <v-list-item class="px-4 py-1">
                        <v-list-item-icon class="mr-3 my-auto">
                          <v-icon small color="primary">
                            mdi-circle-medium
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="text-wrap item-text">
                            {{ getDisplayValue(item) }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider
                        v-if="index < unmappedItems.length - 1"
                        class="mx-4"
                      />
                    </div>
                  </v-list>
                </div>
              </section>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import http from "@/services/http";
import { useSnackbar } from "@/composables/useSnackbar";
import { formatDate } from "@/utils/datetime";

const SECTION_CONFIG = [
  { key: "objetivos", title: "Objetivos", icon: "mdi-flag-checkered", questionIds: [1] },
  { key: "doencas_cronicas", title: "Doenças Crônicas", icon: "mdi-hospital-box", questionIds: [12, 26, 27] },
  { key: "exames", title: "Exames", icon: "mdi-file-document-outline", questionIds: [28, 31] },
  { key: "tipo_sanguineo", title: "Tipo Sanguíneo", icon: "mdi-water", questionIds: [32] },
  { key: "medicamentos", title: "Medicamentos", icon: "mdi-pill", questionIds: [41, 45] },
  { key: "sintomas", title: "Sintomas Frequentes", icon: "mdi-alert-circle-outline", questionIds: [47] },
  { key: "ciclo_menstrual", title: "Ciclo Menstrual", icon: "mdi-calendar-heart", questionIds: [65] },
  { key: "exercicio", title: "Prática de Exercício", icon: "mdi-run", questionIds: [71] },
  { key: "modalidades", title: "Modalidades Esportivas", icon: "mdi-dumbbell", questionIds: [81] },
  { key: "sono", title: "Sono", icon: "mdi-weather-night", questionIds: [94, 99, 102] },
  { key: "biometricos", title: "Dados Biométricos", icon: "mdi-human-male-height", questionIds: [106, 107, 108, 109] },
  { key: "restricoes", title: "Restrições Alimentares", icon: "mdi-food-off", questionIds: [110] },
  { key: "alergias", title: "Alergias", icon: "mdi-alert-outline", questionIds: [111] },
  { key: "transtornos", title: "Transtornos Psicológicos", icon: "mdi-head-snowflake-outline", questionIds: [112] },
  { key: "historico_vida", title: "Histórico de Vida", icon: "mdi-account-clock", questionIds: [113] },
];

const QUESTION_LABELS = {
  106: "Altura",
  107: "Peso",
  108: "IMC",
  109: "Gordura Corporal",
  110: "Restrição Alimentar",
  111: "Alergias",
  112: "Transtorno Psicológico",
  113: "Histórico de Vida",
  45: "Medicamentos",
  26: "Especificação da Doença",
  27: "Especificação",
  31: "Exames",
};

const TYPE_LABEL = {
  1: "Geral",
  2: "Corpo",
  3: "Comida",
  geral: "Geral",
  corpo: "Corpo",
  comida: "Comida",
};

export default {
  name: "AnamnesisDetailsDialog",

  props: {
    value: { type: Boolean, default: false },
    anamneseId: { type: [Number, String], default: null },
  },

  setup() {
    const { error, erroDeRequisicao } = useSnackbar();
    return { error, erroDeRequisicao };
  },

  data() {
    return {
      loading: false,
      anamnese: null,
      histories: [],
      sectionConfig: SECTION_CONFIG,
      questionLabels: QUESTION_LABELS,
    };
  },

  computed: {
    hasData() {
      return this.histories.length > 0;
    },

    groupedSections() {
      return this.sectionConfig.map((section) => ({
        ...section,
        items: this.histories.filter((h) =>
          section.questionIds.includes(h.question_id),
        ),
      }));
    },

    visibleSections() {
      return this.groupedSections.filter((s) => s.items.length > 0);
    },

    mappedIds() {
      return new Set(this.sectionConfig.flatMap((s) => s.questionIds));
    },

    unmappedItems() {
      return this.histories.filter((h) => !this.mappedIds.has(h.question_id));
    },

    headerTitle() {
      const type = this.anamnese?.type;
      const typeLabel = TYPE_LABEL[type] || type || "Anamnese";
      return `Anamnese — ${typeLabel}`;
    },

    headerSubtitle() {
      const parts = [];
      if (this.anamnese?.date) parts.push(this.formatDate(this.anamnese.date));
      if (this.anamnese?.doctor?.name)
        parts.push(`Dr(a). ${this.anamnese.doctor.name}`);
      return parts.join(" • ") || "Detalhes das respostas";
    },
  },

  watch: {
    value(open) {
      if (open && this.anamneseId) {
        this.fetchDetails();
      } else if (!open) {
        // limpa para próximo open
        this.anamnese = null;
        this.histories = [];
      }
    },
    anamneseId(newId) {
      if (newId && this.value) this.fetchDetails();
    },
  },

  methods: {
    formatDate,
    close() {
      this.$emit("input", false);
    },

    async fetchDetails() {
      if (!this.anamneseId) return;
      this.loading = true;
      try {
        const { data } = await http.get(
          `/api/anamneses/${this.anamneseId}/medical-histories`,
        );
        const payload = data?.data || {};
        this.anamnese = payload.anamnese || null;
        this.histories = Array.isArray(payload.histories)
          ? payload.histories
          : [];
      } catch (err) {
        console.error("Erro ao buscar detalhes da anamnese:", err);
        this.erroDeRequisicao(err, "Erro ao carregar respostas da anamnese.");
        this.anamnese = null;
        this.histories = [];
      } finally {
        this.loading = false;
      }
    },

    getDisplayValue(item) {
      if (item.answer_term) return item.answer_term;
      if (item.info_value !== null && item.info_value !== undefined) {
        const label =
          this.questionLabels[item.question_id] ||
          item.question_term ||
          "Informação";
        return `${label}: ${item.info_value}`;
      }
      return "Não informado";
    },

    truncate(str, n) {
      if (!str) return "";
      return str.length > n ? `${str.slice(0, n)}…` : str;
    },
  },
};
</script>

<style scoped>
/* ═══ Dialog ═══ */
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}

.ds-dialog-title {
  font-size: 15px !important;
  font-weight: 700 !important;
  letter-spacing: 0.3px;
  color: var(--ds-white) !important;
  line-height: 1.2;
}

.ds-dialog-subtitle {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  margin-top: 2px;
  letter-spacing: 0.2px;
}

.close-btn {
  position: relative;
  z-index: 1;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

/* ═══ Pílulas de resumo ═══ */
.summary-pill-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.summary-pill {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  background: var(--ds-surface-white);
  border: 1px solid var(--ds-blue-100);
  border-radius: 14px;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--ds-primary);
  letter-spacing: 0.2px;
}

/* ═══ Seções lado a lado: mesma altura na linha ═══ */
.section-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.section-content {
  flex-grow: 1;
  max-height: 260px;
  overflow-y: auto;
}

.section-content::-webkit-scrollbar {
  width: 5px;
}

.section-content::-webkit-scrollbar-track {
  background: transparent;
}

.section-content::-webkit-scrollbar-thumb {
  background: var(--ds-blue-200);
  border-radius: 10px;
}

.section-content::-webkit-scrollbar-thumb:hover {
  background: var(--ds-primary);
}

.item-text {
  font-size: 13px !important;
  line-height: 1.4;
  color: var(--ds-text-600);
}
</style>
